package org.vreo.devicehub.features.core.presentation.stingInputToFloatFormatter
import kotlin.math.max

/**
 * Validates, formats, and clamps a raw string input representing a Float amount.
 *
 * @param value Raw user input.
 * @param maxDecimalPlaces Maximum decimal digits allowed (defaults to 2).
 * @param minAmount Minimum allowed numeric value (optional).
 * @param maxAmount Maximum allowed numeric value (optional).
 * @return Formatted and validated string, or throws an Exception if invalid.
 */
fun validateAndFormatAmount(
    value: String,
    maxDecimalPlaces: Int = 2,
    minAmount: Float? = null,
    maxAmount: Float? = null
): String {
    // Ensure we don't pass a negative decimal constraint
    val decimals = max(0, maxDecimalPlaces)

    if (value.isEmpty()) {
        return value
    }

    try {
        val sanitizedValue = sanitizeAmountInput(value)
        val formattedForDisplay = formatAmountForDisplay(sanitizedValue, decimals)

        // Dynamically build the regex based on the allowed decimal places
        // {0,2} becomes {0,decimals}
        val decimalPattern = if (decimals > 0) "\\.\\d{0,$decimals}" else ""
        val finalRegex = Regex("^(0($decimalPattern)?|[1-9]\\d*($decimalPattern)?|($decimalPattern))?$")

        if (formattedForDisplay.matches(finalRegex) || formattedForDisplay.isEmpty()) {

            // Apply Min/Max Clamping if a full, valid number is present
            val numericValue = formattedForDisplay.toFloatOrNull()
            if (numericValue != null) {
                var clampedValue = numericValue
                if (minAmount != null && clampedValue < minAmount) clampedValue = minAmount
                if (maxAmount != null && clampedValue > maxAmount) clampedValue = maxAmount

                // If clamping changed the value, re-format it to string
                if (clampedValue != numericValue) {
                    return clampedValue.toString().takeWhile { it != '.' } +
                            (clampedValue.toString().substringAfter('.', "").let {
                                if (it.isEmpty() || decimals == 0) "" else ".${it.take(decimals)}"
                            })
                }
            }

            return formattedForDisplay
        } else {
            throw IllegalArgumentException("Invalid amount format: $value")
        }
    } catch (e: IllegalArgumentException) {
        throw e
    } catch (_: Exception) {
        throw Exception("Failed to validate amount: $value")
    }
}

/**
 * Sanitizes raw string input to ensure it contains only digits and at most one decimal point.
 */
private fun sanitizeAmountInput(input: String): String {
    val stringBuilder = StringBuilder()
    var hasDecimal = false
    for (char in input) {
        if (char.isDigit()) {
            stringBuilder.append(char)
        } else if (char == '.' && !hasDecimal) {
            stringBuilder.append(char)
            hasDecimal = true
        }
    }
    return stringBuilder.toString()
}

/**
 * Formats the sanitized amount string for display purposes, handling common input patterns
 * like leading zeros, leading decimal points, and truncating excess decimal places.
 */
private fun formatAmountForDisplay(sanitizedAmount: String, maxDecimalPlaces: Int): String {
    if (sanitizedAmount.isEmpty()) return ""

    var result = sanitizedAmount

    // 1. Handle leading dot: ".5" becomes "0.5" (if decimals are even allowed)
    if (result.startsWith(".")) {
        result = if (maxDecimalPlaces > 0) "0$result" else ""
    }

    // 2. Handle leading zero for non-decimal (e.g., "05" becomes "5", "00" becomes "0")
    if (result.length > 1 && result.startsWith('0') && !result.startsWith("0.")) {
        result = result.dropWhile { it == '0' }
        if (result.isEmpty() || result.startsWith(".")) {
            result = "0$result"
        }
    }

    // 3. Ensure only the allowed decimal places are maintained during input typing
    val parts = result.split('.')
    if (parts.size == 2) {
        if (maxDecimalPlaces == 0) {
            result = parts[0] // Strip decimal entirely if maxDecimalPlaces is 0
        } else if (parts[1].length > maxDecimalPlaces) {
            result = "${parts[0]}.${parts[1].substring(0, maxDecimalPlaces)}"
        }
    }

    return result
}